<script setup>
import { pageTransition } from '~/helpers/animations/animations';

definePageMeta({
  pageTransition: pageTransition,
});

const { t, locale } = useI18n();
const route = useRoute();
const localePath = useLocalePath();

const pageQuery = gql`
  query GetPageData($id: Int!) {
    contact(id: $id) {
      seo_meta_title
      seo_meta_description
      seo_meta_keywords
      slug: seo_url_slug {
        cs
        en
      }
      name
      content
      job
      email
      phone_number
      image
      building {
        id
        name
        slug: seo_url_slug {
          cs
          en
        }
      }
      events {
        id
        name
        image: image_list {
          url
        }
        imageCrop: image_list_crop_to
        slug: seo_url_slug {
          cs
          en
        }
        buildings {
          id
          name
          slug: seo_url_slug {
            cs
            en
          }
        }
        dates {
          id
          start_at {
            timestamp
          }
          end_at {
            timestamp
          }
        }
      }
    }
  }
`;

const { data } = await useAsyncQuery(pageQuery, {
  id: parseInt(route.params.id),
});
const contact = ref(data?.value?.contact);

const breadcrumbs = ref([
  {
    title: t('pages.about.index.breadcrumbs-title'),
    route: 'about',
  },
  {
    title: t('pages.people.index.breadcrumbs-title'),
    route: 'about-people',
  },
  { title: t('pages.people.detail.breadcrumbs-title') },
]);

const photo = computed(() => (contact.value.image ? contact.value.image : '/img/dummy/ngp.png'));
const content = computed(() => (contact.value && contact.value.content ? JSON.parse(contact.value.content).blocks : []));
const phoneLink = computed(() => 'tel:' + contact.value.phone_number.replace(/\s/g, ''));

useHeadData({
  title: contact.value?.seo_meta_title || contact.value?.name || null,
  description: contact.value?.seo_meta_description || null,
  keywords: contact.value?.seo_meta_keywords || null,
  image: contact.value?.image || contact.value?.cropped_detail_image || null,
  url: route.fullPath,
});

// onMounted(() => {
//   if (contact.value.slug) {
//     const store = useStore();
//     store.dispatch('i18n/setRouteParams', {
//       cs: { slug: contact.value.slug.cs },
//       en: { slug: contact.value.slug.en }
//     });
//   }
// });
</script>

<template>
  <div class="main">
    <Breadcrumbs :items="breadcrumbs" />
    <div v-if="contact" class="container-max-content person-profile">
      <div class="person-profile__main">
        <RatioImage :src="photo" :alt="contact.name" />
        <div class="person-profile__data">
          <div class="person-profile__name">
            <h1 class="heading heading--3">{{ contact.name }}</h1>
          </div>
          <div class="person-profile__position">
            <span class="text">{{ contact.job }}</span>
          </div>
          <div class="person-profile__meta">
            <p v-if="contact.building" class="text text--2">
              <nuxt-link
                :to="
                  localePath({
                    name: 'about-buildings-slug',
                    params: { slug: contact.building.slug[locale] },
                  })
                "
                class="link"
              >
                {{ contact.building.name }}
              </nuxt-link>
            </p>
            <p v-if="contact.email" class="text text--2">
              <a :href="`mailto:${contact.email}`" class="link">{{ contact.email }}</a>
            </p>
            <p v-if="contact.phone_number" class="text text--2">
              <a :href="phoneLink" class="link">{{ contact.phone_number }}</a>
            </p>
          </div>
        </div>
      </div>

      <DynamicPageContent :content="content" class="mt-5" />

      <div v-if="contact.events.length" class="mt-5">
        <h2 class="heading heading--2 heading--thin">
          {{ t('pages.people.detail.events') }}
        </h2>
        <EventGrid :data="contact.events" class="mt-4" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.person-profile {
  &__main {
    @include mq($sm) {
      align-items: center;
      display: flex;
    }
  }

  .ratio-image {
    flex-shrink: 0;
    margin-right: 30px;
    width: 208px;

    @include mq($sm, true) {
      margin-bottom: 30px;
    }
  }

  &__data {
  }

  &__name {
    line-height: 1.5;
  }

  &__position {
    margin-bottom: 25px;
  }

  &__meta {
  }

  &__content {
    margin-top: 60px;
  }
}
</style>
